
import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import InvestmentHero from './components/InvestmentHero';
import InvestmentStats from './components/InvestmentStats';
import InvestmentCard from './components/InvestmentCard';
import InvestmentProcess from './components/InvestmentProcess';

export default function InvestmentsPage() {
  useEffect(() => {
    // Update page title and meta description for investments page
    document.title = 'Alabama Glamping Investment Opportunities - High ROI Eco-Tourism | OED Glamping';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover profitable glamping investment opportunities in Alabama with OED Glamping. High ROI eco-tourism investments in sustainable outdoor hospitality. Start your glamping business today with proven returns.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Alabama glamping investment, high ROI eco-tourism, glamping business opportunities, sustainable tourism investment, outdoor hospitality ROI');
    }

    // Update social media images
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', 'https://public.readdy.ai/ai/img_res/c5cb671fd5f08b8b697930caaee5ab16.jpg');
    }

    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', 'https://readdy.ai/api/search-image?query=Financial%20charts%20and%20graphs%20showing%20high%20ROI%20returns%20for%20glamping%20investments%20with%20luxury%20eco-friendly%20accommodations%20in%20Alabama%20forest%20background%2C%20professional%20investment%20presentation%20with%20golden%20sunset%20lighting%2C%20sustainable%20tourism%20business%20opportunity%20visualization&width=1200&height=630&seq=investments-twitter&orientation=landscape');
    }

    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://oedglampire.com/investments');
    }

    // Update Open Graph URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://oedglampire.com/investments');
    }

    // Update Twitter URL
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', 'https://oedglampire.com/investments');
    }

    // Add Investment Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "InvestmentOrSavingsProduct",
      "name": "Alabama Glamping Investment Opportunities",
      "description": "Profitable glamping investment opportunities in Alabama with high ROI eco-tourism investments in sustainable outdoor hospitality.",
      "url": "https://oedglampire.com/investments",
      "provider": {
        "@type": "Organization",
        "name": "OED Glamping"
      },
      "feesAndCommissionsSpecification": "Contact for detailed investment terms",
      "annualPercentageYield": "15-25% projected ROI"
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('InvestmentOrSavingsProduct')) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const investments = [
    {
      id: "blue-ridge",
      title: "Blue Ridge Mountain Retreat",
      location: "North Georgia Mountains",
      roi: "22%",
      investment: "$125,000",
      image: "https://readdy.ai/api/search-image?query=Luxury%20glamping%20domes%20nestled%20in%20misty%20Blue%20Ridge%20Mountains%20with%20autumn%20foliage%2C%20eco-friendly%20sustainable%20accommodations%20surrounded%20by%20pristine%20forest%20landscape%2C%20golden%20hour%20lighting%20creating%20warm%20atmospheric%20glow%2C%20professional%20real%20estate%20photography%20showcasing%20premium%20outdoor%20hospitality%20investment%20opportunity&width=400&height=300&seq=blue-ridge-1&orientation=landscape",
      features: ["Mountain Views", "Hiking Trails", "Luxury Amenities"],
      description: "Premium mountain glamping experience with stunning Blue Ridge views and world-class amenities."
    },
    {
      id: "redwood-forest",
      title: "Redwood Forest Sanctuary",
      location: "Northern California",
      roi: "18%",
      investment: "$150,000",
      image: "https://readdy.ai/api/search-image?query=Elegant%20glamping%20tents%20among%20towering%20ancient%20redwood%20trees%20in%20Northern%20California%2C%20sustainable%20eco-luxury%20accommodations%20with%20natural%20wood%20elements%2C%20filtered%20sunlight%20through%20massive%20tree%20canopy%2C%20serene%20forest%20atmosphere%20with%20premium%20outdoor%20hospitality%20design&width=400&height=300&seq=redwood-1&orientation=landscape",
      features: ["Ancient Redwoods", "Eco-Luxury", "Nature Immersion"],
      description: "Immersive forest experience among ancient redwoods with sustainable luxury accommodations."
    },
    {
      id: "desert-oasis",
      title: "Desert Oasis Retreat",
      location: "Arizona Sonoran Desert",
      roi: "25%",
      investment: "$110,000",
      image: "https://readdy.ai/api/search-image?query=Modern%20glamping%20structures%20in%20stunning%20Arizona%20Sonoran%20Desert%20landscape%20with%20saguaro%20cacti%20and%20dramatic%20rock%20formations%2C%20luxury%20eco-friendly%20accommodations%20under%20starlit%20desert%20sky%2C%20warm%20golden%20sunset%20lighting%2C%20premium%20desert%20hospitality%20investment%20opportunity&width=400&height=300&seq=desert-1&orientation=landscape",
      features: ["Desert Views", "Stargazing", "Unique Landscape"],
      description: "Unique desert glamping with spectacular stargazing and iconic Sonoran landscape."
    },
    {
      id: "lowcountry-lodge",
      title: "Lowcountry Lodge",
      location: "South Carolina Coast",
      roi: "20%",
      investment: "$135,000",
      image: "https://readdy.ai/api/search-image?query=Elegant%20glamping%20accommodations%20in%20South%20Carolina%20Lowcountry%20with%20Spanish%20moss%20draped%20oak%20trees%20and%20coastal%20marshlands%2C%20luxury%20eco-friendly%20structures%20reflecting%20Southern%20charm%2C%20warm%20golden%20hour%20lighting%2C%20premium%20coastal%20hospitality%20investment%20opportunity&width=400&height=300&seq=lowcountry-1&orientation=landscape",
      features: ["Coastal Access", "Southern Charm", "Wildlife Viewing"],
      description: "Southern coastal glamping with access to pristine beaches and rich wildlife."
    },
    {
      id: "tennessee-valley",
      title: "Tennessee Valley Vista",
      location: "East Tennessee",
      roi: "19%",
      investment: "$120,000",
      image: "https://readdy.ai/api/search-image?query=Luxury%20glamping%20domes%20overlooking%20Tennessee%20Valley%20with%20rolling%20hills%20and%20misty%20mountains%2C%20sustainable%20eco-friendly%20accommodations%20surrounded%20by%20lush%20green%20landscape%2C%20warm%20sunrise%20lighting%2C%20premium%20Appalachian%20hospitality%20investment%20opportunity&width=400&height=300&seq=tennessee-1&orientation=landscape",
      features: ["Valley Views", "Music Heritage", "Outdoor Adventures"],
      description: "Appalachian glamping with rich music heritage and stunning valley vistas."
    },
    {
      id: "alpine-meadows",
      title: "Alpine Meadows Resort",
      location: "Colorado Rockies",
      roi: "24%",
      investment: "$160,000",
      image: "https://readdy.ai/api/search-image?query=Premium%20glamping%20structures%20in%20Colorado%20Rocky%20Mountain%20alpine%20meadows%20with%20snow-capped%20peaks%20and%20wildflower%20fields%2C%20luxury%20eco-friendly%20accommodations%20at%20high%20altitude%2C%20crystal%20clear%20mountain%20air%20and%20dramatic%20landscape%2C%20professional%20mountain%20hospitality%20photography&width=400&height=300&seq=alpine-1&orientation=landscape",
      features: ["Alpine Setting", "Ski Access", "Mountain Adventures"],
      description: "High-altitude glamping with year-round outdoor activities and breathtaking alpine scenery."
    },
    {
      id: "alabama-gulf",
      title: "Alabama Gulf Shores",
      location: "Gulf Coast Alabama",
      roi: "21%",
      investment: "$140,000",
      image: "https://readdy.ai/api/search-image?query=Luxury%20glamping%20accommodations%20near%20Alabama%20Gulf%20Shores%20with%20pristine%20white%20sand%20beaches%20and%20emerald%20waters%2C%20eco-friendly%20coastal%20structures%20with%20Southern%20hospitality%20design%2C%20warm%20sunset%20lighting%20over%20Gulf%20of%20Mexico%2C%20premium%20beachfront%20investment%20opportunity&width=400&height=300&seq=gulf-1&orientation=landscape",
      features: ["Beach Access", "Gulf Views", "Water Sports"],
      description: "Coastal Alabama glamping with pristine beaches and world-class fishing."
    },
    {
      id: "georgia-peach",
      title: "Georgia Peach Orchard",
      location: "North Georgia",
      roi: "17%",
      investment: "$115,000",
      image: "https://readdy.ai/api/search-image?query=Charming%20glamping%20accommodations%20in%20Georgia%20peach%20orchard%20with%20blooming%20fruit%20trees%20and%20rolling%20countryside%2C%20sustainable%20eco-friendly%20structures%20surrounded%20by%20agricultural%20beauty%2C%20warm%20spring%20lighting%20with%20pink%20blossoms%2C%20premium%20agritourism%20investment%20opportunity&width=400&height=300&seq=peach-1&orientation=landscape",
      features: ["Orchard Setting", "Farm-to-Table", "Seasonal Beauty"],
      description: "Agritourism glamping in working peach orchards with farm-to-table experiences."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <header>
          <h1 className="sr-only">Alabama Glamping Investment Opportunities - High ROI Eco-Tourism</h1>
          <InvestmentHero />
        </header>
        <section aria-label="Investment Statistics">
          <InvestmentStats />
        </section>
        <section aria-label="Investment Opportunities" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                <strong>Current Investment Opportunities</strong>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore our curated selection of <strong>high-ROI glamping investments</strong> across prime locations in Alabama and the Southeast.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {investments.map((investment) => (
                <InvestmentCard key={investment.id} investment={investment} />
              ))}
            </div>
          </div>
        </section>
        <section aria-label="Investment Process">
          <InvestmentProcess />
        </section>
      </main>
      <Footer />
    </div>
  );
}
